// install-prompt.ts — the "Install Warfare / Add to Home Screen" affordance for
// the TS web client (Phase 3 of tasks/pwa-install-plan.md). A thin dismissable
// DOM banner over the canvas — NOT a canvas form — because the install APIs live
// in DOM-world:
//
//   - Chromium (Android + desktop): capture the `beforeinstallprompt` event,
//     stash it, and show an "Install" button whose tap calls the stashed
//     event's prompt(). The browser owns the actual install UI.
//   - iOS Safari: no programmatic prompt exists, so show concise
//     "tap Share → Add to Home Screen" instructions with the Share glyph.
//
// It suppresses itself when already installed (display-mode: standalone /
// navigator.standalone) and snoozes for a couple weeks after a dismissal, so it
// never nags. Nothing here touches the game canvas or the forms system.
//
// Wiring (main.ts): call armInstallPrompt() early (so the beforeinstallprompt
// listener is attached before the event can fire) and maybeShowInstallPrompt()
// once the main menu is up (boot complete).

// The Chromium-only event. Typed locally — it is not in the standard lib DOM types.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const SNOOZE_KEY = 'wi.install.snoozeUntil';
const INSTALLED_KEY = 'wi.install.done';
const SNOOZE_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
// Let the menu settle before the banner appears (don't pop the same paint).
const SHOW_DELAY_MS = 1200;

let deferred: BeforeInstallPromptEvent | null = null;
let menuReady = false;
let armed = false;
let banner: HTMLElement | null = null;

function isStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
}

function isIOS(): boolean {
  const ua = window.navigator.userAgent;
  // iPhone/iPod report directly; iPadOS masquerades as Macintosh, so detect a
  // touch-capable "Mac" too.
  return (
    /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1)
  );
}

function readNum(key: string): number {
  try {
    const v = window.localStorage.getItem(key);
    return v ? Number(v) || 0 : 0;
  } catch {
    return 0;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — non-fatal */
  }
}

function snoozed(): boolean {
  return readNum(SNOOZE_KEY) > Date.now();
}

function snooze(): void {
  write(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * DAY_MS));
}

// Arm the capture/lifecycle listeners. Idempotent; safe to call before boot.
export function armInstallPrompt(): void {
  if (armed) return;
  armed = true;
  if (isStandalone()) {
    write(INSTALLED_KEY, '1');
    return;
  }
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Stop Chromium's mini-infobar; we present our own affordance.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    if (menuReady) showBanner();
  });
  window.addEventListener('appinstalled', () => {
    write(INSTALLED_KEY, '1');
    deferred = null;
    hideBanner();
  });
}

// Called once the main menu is visible (boot complete). Decides whether to show.
export function maybeShowInstallPrompt(): void {
  menuReady = true;
  if (isStandalone() || readNum(INSTALLED_KEY) === 1 || snoozed()) return;
  // Chromium: show only once we have captured a prompt to fire. iOS: show the
  // manual instructions. Other browsers (e.g. desktop Firefox): show nothing.
  if (deferred || isIOS()) {
    window.setTimeout(() => {
      if (!menuReady || isStandalone()) return;
      showBanner();
    }, SHOW_DELAY_MS);
  }
}

const SHARE_ICON =
  '<svg viewBox="0 0 50 50" width="20" height="20" aria-hidden="true" ' +
  'style="vertical-align:-4px;fill:none;stroke:#7cf;stroke-width:3;' +
  'stroke-linecap:round;stroke-linejoin:round">' +
  '<path d="M25 5 L25 32 M16 14 L25 5 L34 14"/>' +
  '<path d="M14 22 L9 22 L9 44 L41 44 L41 22 L36 22"/></svg>';

function ensureStyle(): void {
  if (document.getElementById('wi-install-style')) return;
  const css = document.createElement('style');
  css.id = 'wi-install-style';
  css.textContent = `
#wi-install {
  position: fixed; left: 50%; transform: translateX(-50%);
  bottom: calc(12px + env(safe-area-inset-bottom));
  z-index: 2147483000; width: min(440px, calc(100vw - 24px));
  box-sizing: border-box; display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 12px;
  background: #141a14; color: #e8ffe8;
  border: 1px solid #2f6b2f; box-shadow: 0 6px 24px rgba(0,0,0,.55);
  font: 13px/1.45 -apple-system, system-ui, "Segoe UI", Roboto, sans-serif;
}
#wi-install .wi-ic { flex: 0 0 auto; width: 40px; height: 40px; border-radius: 9px; }
#wi-install .wi-tx { flex: 1 1 auto; min-width: 0; }
#wi-install .wi-tx b { color: #fff; font-weight: 600; }
#wi-install .wi-btn {
  flex: 0 0 auto; appearance: none; border: 0; cursor: pointer;
  background: #2f8f2f; color: #fff; font: inherit; font-weight: 600;
  padding: 8px 14px; border-radius: 8px;
}
#wi-install .wi-btn:hover { background: #36a836; }
#wi-install .wi-x {
  flex: 0 0 auto; appearance: none; border: 0; cursor: pointer;
  background: transparent; color: #9fd49f; font-size: 20px; line-height: 1;
  padding: 4px 6px; border-radius: 6px;
}
#wi-install .wi-x:hover { color: #fff; }
`;
  document.head.appendChild(css);
}

function hideBanner(): void {
  banner?.remove();
  banner = null;
}

function showBanner(): void {
  if (banner) return;
  ensureStyle();

  const el = document.createElement('div');
  el.id = 'wi-install';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Install Warfare Incorporated');

  const icon = document.createElement('img');
  icon.className = 'wi-ic';
  icon.src = '/icon-192.png';
  icon.alt = '';
  el.appendChild(icon);

  const tx = document.createElement('div');
  tx.className = 'wi-tx';
  el.appendChild(tx);

  const close = document.createElement('button');
  close.className = 'wi-x';
  close.type = 'button';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '×';
  close.addEventListener('click', () => {
    snooze();
    hideBanner();
  });

  if (deferred) {
    // Chromium: real install button.
    tx.innerHTML =
      '<b>Install Warfare</b><br>Add it to your home screen for full-screen, one-tap play.';
    const btn = document.createElement('button');
    btn.className = 'wi-btn';
    btn.type = 'button';
    btn.textContent = 'Install';
    btn.addEventListener('click', () => {
      void doInstall();
    });
    el.appendChild(btn);
    el.appendChild(close);
  } else {
    // iOS Safari: manual instructions (no programmatic prompt exists).
    tx.innerHTML =
      '<b>Add Warfare to your Home Screen</b><br>Tap ' +
      SHARE_ICON +
      ' <b>Share</b>, then <b>Add to Home Screen</b>.';
    el.appendChild(close);
  }

  document.body.appendChild(el);
  banner = el;
}

async function doInstall(): Promise<void> {
  const e = deferred;
  deferred = null; // a beforeinstallprompt event can only be prompted once
  hideBanner();
  if (!e) return;
  try {
    await e.prompt();
    const choice = await e.userChoice;
    if (choice.outcome === 'dismissed') snooze();
  } catch {
    /* prompt unavailable — leave the snooze untouched so we can offer again */
  }
}
